/**
 * Bloco "Dados Cadastrais" da conta da organização (EC, RS e CP) — #1014.
 *
 * Lê o contrato tipado que `GET /organizations/{id}` devolve em `dados_cadastrais` (o leitor
 * único do backend, que entende as chaves novas E as legadas), e não mais `settings` cru:
 * depois da #1014 o auto-cadastro deixa de gravar `settings.document`, e `settings.address`
 * passa a ser a rua (string) — as três cópias antigas do bloco mostrariam CNPJ ausente e
 * endereço vazio.
 *
 * Janela de deploy (Pages antes do Fly): sem `dados_cadastrais` na resposta, cai no
 * `settings` legado, como antes.
 */

import type { DadosCadastraisDoCliente } from '../services/resellerCustomerOrgApi'
import { PORTES, SETORES, formatCep, formatDocument, formatPhone } from '../utils/dadosCadastrais'

interface Props {
  dadosCadastrais?: DadosCadastraisDoCliente | null
  /** `settings` cru da org — só para a janela em que a API ainda não manda `dados_cadastrais`. */
  settings?: Record<string, unknown> | null
}

const rotulo = (
  lista: ReadonlyArray<{ value: string; label: string }>,
  value: string | null | undefined,
) => (value ? (lista.find((i) => i.value === value)?.label ?? value) : null)

function deLegado(settings: Record<string, unknown>): DadosCadastraisDoCliente {
  const address = settings.address as Record<string, unknown> | string | undefined
  const end = address && typeof address === 'object' ? address : null
  return {
    document:
      (settings.document as string) ??
      (settings.cnpj as string) ??
      (settings.cpf as string) ??
      null,
    document_type: null,
    email: (settings.email as string) ?? null,
    phone: (settings.phone as string) ?? null,
    size: (settings.size as string) ?? null,
    industry: (settings.industry as string) ?? null,
    address: end
      ? {
          street: (end.street as string) ?? null,
          number: null,
          complement: null,
          district: null,
          city: (end.city as string) ?? null,
          state: (end.state as string) ?? null,
          zip_code: (end.zip_code as string) ?? null,
          country: (end.country as string) ?? null,
        }
      : typeof address === 'string' && address
        ? {
            street: address,
            number: (settings.address_number as string) ?? null,
            complement: (settings.complement as string) ?? null,
            district: (settings.province as string) ?? null,
            city: (settings.city as string) ?? null,
            state: (settings.state as string) ?? null,
            zip_code: (settings.postal_code as string) ?? null,
            country: (settings.country as string) ?? null,
          }
        : null,
  }
}

export function enderecoEmLinha(end: DadosCadastraisDoCliente['address']): string {
  if (!end) return ''
  const rua = [end.street, end.number].filter(Boolean).join(', ')
  const partes = [
    rua,
    end.complement,
    end.district,
    end.city,
    end.state,
    end.zip_code ? formatCep(end.zip_code) : null,
  ]
  return partes.filter(Boolean).join(' · ')
}

export function DadosCadastraisDaOrganizacao({ dadosCadastrais, settings }: Props) {
  const dados =
    dadosCadastrais ?? (settings && Object.keys(settings).length > 0 ? deLegado(settings) : null)
  if (!dados) return null
  const linhas: Array<{ rotulo: string; valor: string | null; largo?: boolean }> = [
    { rotulo: 'CNPJ / CPF', valor: dados.document ? formatDocument(dados.document) : null },
    { rotulo: 'E-mail de contato', valor: dados.email },
    { rotulo: 'Telefone', valor: dados.phone ? formatPhone(dados.phone) : null },
    { rotulo: 'Setor', valor: rotulo(SETORES, dados.industry) },
    { rotulo: 'Porte', valor: rotulo(PORTES, dados.size) },
    { rotulo: 'Endereço', valor: enderecoEmLinha(dados.address) || null, largo: true },
  ].filter((l) => l.valor)
  if (linhas.length === 0) return null

  return (
    <div>
      <h3 className="mb-4 text-lg font-medium text-gray-900">Dados Cadastrais</h3>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {linhas.map((l) => (
          <div key={l.rotulo} className={l.largo ? 'md:col-span-2' : undefined}>
            <label className="mb-1 block text-sm font-medium text-gray-700">{l.rotulo}</label>
            <p className="text-gray-900">{l.valor}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
